<template>
  <div :class="[$options.name, { mobile: isMobile }, { disabled: busy }]">
    <template v-if="!submitted">
      <h1 class="tw:heading-1" style="font-weight: 400; margin: 0 0 0.25rem">
        {{ passwordAction ? translations.expired.title : translations.title }}
      </h1>
      <p style="font-size: 1rem; font-weight: 300; margin: 0 0 2rem">
        {{ passwordAction ? translations.expired.subtitle : translations.subtitle }}
      </p>
      <EdroneInput
        id="email-input"
        v-model.trim="v$.userEmail.$model"
        :error-msg="
          v$.userEmail.$dirty && v$.userEmail.$error
            ? translations.inputs.email.errors[v$.userEmail.$errors[0].$message]
            : null
        "
        :invalid="v$.userEmail.$dirty && (v$.userEmail.email.$invalid || v$.userEmail.required.$invalid)"
        :label="translations.inputs.email.label"
        :placeholder="translations.inputs.email.placeholder"
        class="required-inputs"
        unique-id="83e0d56f-8dca-4317-b869-5aa2c18f6cd7"
        @enter="handleNextInput"
        @input="handleEmailInput"
      />
      <a :href="translations.inputs.email.hintlink" class="tw:caption tw:pt-2" target="_blank">
        {{ translations.inputs.email.hint }}
      </a>
      <div class="tw:pt-6">
        <EdroneButton
          :disabled="userEmail && !v$.userEmail.$error ? false : true"
          class="required-inputs tw:mb-4"
          style="width: 100%"
          type="solid"
          unique-id="1580a06a-3457-4ecc-b955-01bbcbcde19b"
          @click="printResult"
        >
          {{ translations.buttons.confirm }}
        </EdroneButton>
      </div>
    </template>
    <template v-else>
      <h1 class="tw:heading-1" style="font-weight: 400; margin: 0 0 0.25rem">
        {{ translations.submitted.title }}
      </h1>
      <p style="font-size: 1rem; font-weight: 300; margin: 0 0 2rem">
        {{ translations.submitted.subtitle }}
      </p>
      <EdroneButton
        class="tw:mb-2"
        style="width: 100%"
        type="text"
        unique-id="36d3f524-2b0a-4105-80c8-16ac6ac17a4c"
        @click="openInbox"
      >
        {{ translations.submitted.buttons.open + " GMAIL" }}
      </EdroneButton>
      <EdroneButton
        class="tw:mb-2"
        style="width: 100%"
        type="text"
        unique-id="380521e0-9deb-4de9-bea7-a1cd1eb20fcd"
        @click="openInbox('yahoo')"
      >
        {{ translations.submitted.buttons.open + " Yahoo" }}
      </EdroneButton>
      <EdroneButton
        class="tw:mb-4"
        style="width: 100%"
        type="text"
        unique-id="6056d20f-9ed5-4c3d-a59c-522cc9615934"
        @click="openInbox('outlook')"
      >
        {{ translations.submitted.buttons.open + " Outlook" }}
      </EdroneButton>
    </template>
    <EdroneButton type="text" unique-id="838a9b19-6ac9-48f5-b7bd-04835711aeab" @click="handleBack">
      <template #left-icon>
        <font-awesome-icon icon="fa-solid fa-chevron-left" />
      </template>
      {{ translations.buttons.back }}
    </EdroneButton>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import axios from "vue-app/utils/axios";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";

export default defineComponent({
  name: "AccountForgotPasswordForm",
  components: {
    EdroneInput,
    EdroneButton,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    passwordAction: {
      type: String,
      default: "",
    },
  },
  setup() {
    const translations = window._trans._account["password-management"].forgot;
    const submitted = ref(false);

    const userEmail = ref("");

    const rules = {
      userEmail: { required, email },
    };
    const v$ = useVuelidate(rules, { userEmail });

    function handleEmailInput() {
      // console.log(userEmail.value)
    }

    function handleNextInput(event: KeyboardEvent) {
      const inputs = document.querySelectorAll(".required-inputs input, .required-inputs.edrone-button");
      const index = Array.from(inputs).indexOf(event.target as HTMLElement);
      if (index === 0) {
        v$.value.userEmail.$touch();
      }
      (inputs[index + 1] as HTMLElement)?.focus();
      if (index === 0 && !!userEmail.value) {
        printResult();
      }
    }

    const busy = ref<boolean>(false);

    function printResult() {
      v$.value.userEmail.$touch();

      if (!v$.value.userEmail.$error) {
        console.log("result:", {
          email: userEmail.value,
        });
        resetPassword();
      }
    }

    async function resetPassword() {
      try {
        busy.value = true;
        let bodyFormData = new FormData();
        bodyFormData.append("email", userEmail.value);

        const response = await axios({
          method: "post",
          url: "/forgot_password",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          submitted.value = true;
          console.log("success", response);
        } else {
          console.log("error", response.data.error);
        }
        busy.value = false;
      } catch (error) {
        console.log("error", error);
        busy.value = false;
      }
    }

    function openInbox(inbox = "gmail") {
      if (inbox === "gmail") {
        window.open("https://mail.google.com", "_blank");
      }
      if (inbox === "yahoo") {
        window.open("https://mail.yahoo.com", "_blank");
      }
      if (inbox === "outlook") {
        window.open("https://outlook.live.com/mail/", "_blank");
      }
    }

    function handleBack() {
      window.location.href = "/login";
    }

    return {
      submitted,
      printResult,
      busy,
      translations,
      userEmail,
      handleEmailInput,
      v$,
      handleNextInput,
      openInbox,
      handleBack,
    };
  },
});
</script>

<style lang="scss" scoped></style>
